import axios from 'axios'
import router from '../router'
import cookie from './cookie'
import { message } from '../commonJs/ResetMessage'
import config from '../config'

// Token 存储 key 定义
const token = {
  token_key: '28project_usertoken',
  token_role_name: '28project_userRolename',
  token_usrname: '28project_username',
  refresh_key: '28project_refreshToken'
};

// 后台返回码定义
const backCode = {
  SUCCESS: 0,
  TOKEN_NOT_EXIST: 1281,
  TOKEN_CHECK_FAILED: 1282,
  TOKEN_TIMEOUT: 1283
};

// localStorage 安全读写（隐私模式/异常时静默失败，不影响主流程）
function lsGet(key) {
  try { return localStorage.getItem(key) || ''; } catch (e) { return ''; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, val); } catch (e) {}
}
function lsRemove(key) {
  try { localStorage.removeItem(key); } catch (e) {}
}

/**
 * 登录态存储策略：localStorage 为权威源，cookie 仅作镜像。
 * 微信 WebView 在页面被系统回收后重新加载时，持久 cookie 常被丢弃，
 * 而 localStorage 更耐用；重载时用 localStorage 把 token 回写到 cookie，
 * 这样原生 XHR 下载（依赖浏览器自动带 cookie 鉴权）与请求头都能正常工作。
 * 若 cookie 有而 localStorage 无，则反向补写 localStorage，保证两端一致。
 */
function getToken() {
  const ls = lsGet(token.token_key);
  const ck = cookie.getCookie(token.token_key) || '';
  if (ls) {
    if (!ck) cookie.setCookie(token.token_key, ls); // 回写 cookie 镜像
    return ls;
  }
  if (ck) {
    lsSet(token.token_key, ck); // 反向补写 localStorage
    return ck;
  }
  return '';
}

/**
 * 读取当前 refresh token（localStorage 权威 + cookie 镜像）
 */
function getRefreshToken() {
  const ls = lsGet(token.refresh_key);
  const ck = cookie.getCookie(token.refresh_key) || '';
  if (ls) {
    if (!ck) cookie.setCookie(token.refresh_key, ls);
    return ls;
  }
  if (ck) {
    lsSet(token.refresh_key, ck);
    return ck;
  }
  return '';
}

/**
 * 写入登录态：localStorage 与 cookie 双写（localStorage 权威 + cookie 镜像）
 * @param {string} tokenVal 访问令牌
 * @param {string} refreshVal 刷新令牌
 */
function saveLoginState(tokenVal, refreshVal) {
  if (tokenVal) {
    cookie.setCookie(token.token_key, tokenVal);
    lsSet(token.token_key, tokenVal);
  }
  if (refreshVal) {
    cookie.setCookie(token.refresh_key, refreshVal);
    lsSet(token.refresh_key, refreshVal);
  }
}

/**
 * 清理登录态：cookie 与 localStorage 一并清除（保留用户名 cookie 以便自动填充）
 * 同时清除用户信息 28project_UsrInfo，确保退出登录 / token 失效后页面不再残留旧用户数据
 */
function clearLoginState() {
  cookie.removeCookie(token.token_key);
  cookie.removeCookie(token.refresh_key);
  cookie.removeCookie('28project_UsrInfo');
  lsRemove(token.token_key);
  lsRemove(token.refresh_key);
}

/**
 * 是否已登录：cookie 中存在 token 即视为已登录
 */
function isLoggedIn() {
  return !!getToken();
}

// Token刷新状态管理
const tokenRefreshState = {
  isRefreshing: false,  // 是否正在刷新token
  refreshPromise: null, // 刷新token的Promise
  waitingRequests: []   // 等待token刷新完成的请求队列
};

/**
 * 提示函数
 * @param {string} msg - 提示消息
 * @param {string} type - 类型：success/warning/error/info
 * @param {number} time - 显示时长（毫秒）
 */
function tip(msg, type = 'error', time = 2000) {
  message({
    message: msg,
    type,
    duration: time
  });
}

/**
 * 跳转到登录页（token 失效时使用）
 * 先清空本地登录态，否则路由守卫会因 isLoggedIn() 仍为 true 又把页面弹回首页，
 * 表现为“只提示不跳转”。再按设备类型跳到对应登录页（移动端 /m/login，PC /login）。
 * @param {string} redirect - 重定向路径
 */
function redirectToLogin(redirect) {
  clearLoginState();
  goLogin(redirect);
}

/**
 * 解析后台返回码
 * @param {object} data - 响应数据
 * @param {function} callBack - 回调函数
 * @param {*} param - 回调参数
 */
function analysisReturnCode(data, callBack, param = null) {
  const { code, message, msg } = data;

  switch (code) {
    case 202:
    case 203:
    case 403:
    case backCode.TOKEN_CHECK_FAILED:
      tip('登录信息已失效，请重新登录');
      redirectToLogin(router.currentRoute.fullPath);
      break;

    case backCode.TOKEN_NOT_EXIST:
      tip('token不存在，请重新登录');
      redirectToLogin(router.currentRoute.fullPath);
      break;

    case 205:
    case 'ERR_CHECK_TOKEN':
      tip('当前账号被顶替，请重新登录');
      redirectToLogin(router.currentRoute.fullPath);
      break;

    case 204:
    case 'ERR_TIMEOUT_TOKEN':
      // 不再作为统一的 token 超时处理，仅提示错误
      tip(message || msg || '登录信息异常，请重新登录');
      redirectToLogin(router.currentRoute.fullPath);
      break;

    case backCode.TOKEN_TIMEOUT:
      // 仅当 code === 1283 时认为 token 超时并尝试刷新
      refresh(callBack, param);
      break;

    default:
      tip(message || msg);
  }
}


/**
 * 刷新 Token
 * @param {function} callBack - 刷新成功后的回调
 * @param {*} param - 回调参数
 */
function refresh(callBack, param) {
  // 如果正在刷新，将请求加入等待队列
  if (tokenRefreshState.isRefreshing) {
    tokenRefreshState.waitingRequests.push({ callBack, param });
    return;
  }

  tokenRefreshState.isRefreshing = true;

  const refreshToken = getRefreshToken();
  // 临时以 refreshToken 作为请求凭证（后台 RefreshToken 接口接受 refreshToken 作为 Authorization）
  cookie.setCookie(token.token_key, refreshToken);
  lsSet(token.token_key, refreshToken);

  const apiRoot = config.getApiRootSync();
  axios.post(`${apiRoot}/Usr/RefreshToken`, {}, {
    headers: { 'Authorization': getToken() }
  })
  .then(res => {
    if (res.data.code === backCode.SUCCESS) {
      // 刷新成功，更新 token（双写 localStorage + cookie 镜像）
      const newToken = res.data.data.strAuthorization;
      saveLoginState(newToken, '');

      // 执行等待队列中的所有回调
      tokenRefreshState.waitingRequests.forEach(({ callBack, param }) => {
        if (callBack) {
          callBack(param);
        }
      });
      tokenRefreshState.waitingRequests = [];

      // 执行当前回调
      if (callBack) {
        callBack(param);
      }
    } else {
      handleRefreshFailure();
    }
  })
  .catch(error => {
    console.error('Token refresh error:', error);
    handleRefreshFailure();
  })
  .finally(() => {
    tokenRefreshState.isRefreshing = false;
    tokenRefreshState.refreshPromise = null;
    tokenRefreshState.waitingRequests = [];
  });
}

/**
 * 处理 Token 刷新失败
 */
function handleRefreshFailure() {
  tip('登录信息已失效，请重新登录');
  redirectToLogin(router.currentRoute.fullPath);
}

/**
 * 文件流下载
 * @param {string} url - 文件链接
 * @param {string} fileName - 文件名
 * @param {string} type - 文件类型
 */
function fileLinkToStreamDownload(url, fileName, type) {
  if (!url) {
    throw new Error('文件链接不能为空');
  }

  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.setRequestHeader('Content-Type', `application/${type}`);
  xhr.responseType = 'blob';

  xhr.onload = function() {
    if (this.status === 200) {
      downloadExportFile(this.response, fileName, type);
    }
  };

  xhr.send();
}

/**
 * 下载文件
 * @param {Blob|string} blob - 文件 Blob 或 URL
 * @param {string} fileName - 文件名
 * @param {string} fileType - 文件类型
 */
function downloadExportFile(blob, fileName, fileType) {
  const link = document.createElement('a');

  if (typeof blob === 'string') {
    link.href = blob;
    link.target = '_blank';
  } else {
    link.href = URL.createObjectURL(blob);
  }

  link.download = `${fileName}.${fileType}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (typeof blob !== 'string') {
    URL.revokeObjectURL(link.href);
  }
}

/**
 * 下载 PDF 文件
 * @param {string} pdfUrl - PDF 文件链接
 * @param {string} name - 文件名（不包含扩展名）
 */
function downLoadPDF(pdfUrl, name) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', pdfUrl, true);
  xhr.responseType = 'blob';

  xhr.onload = () => {
    if (xhr.status === 200) {
      const blob = new Blob([xhr.response], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
    }
  };

  xhr.send();
}

/**
 * 跳转到登录页（按当前设备类型自动区分 PC / 移动端）
 * @param {string} [redirect] 登录成功后重定向的路径
 */
function goLogin(redirect) {
  const curPath = (router.currentRoute && router.currentRoute.path) || '';
  const isMobile = sessionStorage.getItem('isMobile') === 'true' || curPath.startsWith('/m/');
  const path = isMobile ? '/m/login' : '/login';
  if (redirect) {
    router.replace({ path, query: { redirect } });
  } else {
    router.replace({ path });
  }
}

/**
 * 获取枚举常量
 * @returns {object|null} 枚举常量对象
 */
function getConst() {
  const constData = cookie.getCookie('28Project_Const');
  return constData ? JSON.parse(constData) : null;
}

/**
 * 根据枚举类型和值获取枚举项
 * @param {string} type - 枚举类型
 * @param {number} value - 枚举值
 * @returns {object|null} 枚举项对象
 */
function getConstItem(type, value) {
  const constData = getConst();
  if (!constData || !constData[type]) {
    return null;
  }

  let list = constData[type];

  // 兼容类型：
  // 1. 直接数组，例如: orderState: [ {...} ]
  // 2. 对象中包含 heart 数组，例如: rptLevel: { heart: [ {...} ] }
  if (!Array.isArray(list) && list && Array.isArray(list.heart)) {
    list = list.heart;
  }

  if (!Array.isArray(list)) {
    return null;
  }

  return list.find(item => item.value == value) || null;
}


export default {
  tip,
  analysisReturnCode,
  token,
  refresh,
  fileLinkToStreamDownload,
  downLoadPDF,
  getConst,
  getConstItem,
  backCode,
  tokenRefreshState,
  getToken,
  getRefreshToken,
  saveLoginState,
  clearLoginState,
  isLoggedIn,
  goLogin
}



// WEBPACK FOOTER //
// ./src/service/public.js